import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { IconButton, Stack, Typography } from '@monorepo/nova-ui';
import { UnavailableValue } from '@monorepo/types';
import { COPY_FEEDBACK_DURATION, PASSWORD_DISPLAY } from '../constants';
import {
  getIconButtonStyles,
  getLabelStyles,
  getPasswordTextStyles,
} from '../styles';
import { InfoRowProps } from '../types';
import { AnimatedPasswordText } from './animated-password-text';

export const InfoRow: FunctionComponent<InfoRowProps> = ({
  label,
  value,
  copyable = false,
  isPassword = false,
  showPassword = false,
  onTogglePassword,
  onCopy,
  tsUser,
}) => {
  const { t } = useTranslation(['login']);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyClick = () => {
    const copyValue =
      isPassword && !showPassword ? tsUser?.password || '' : value;
    if (onCopy) {
      onCopy(copyValue);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), COPY_FEEDBACK_DURATION);
    }
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      id="info-row"
      width="100%"
      gap={2}
    >
      <Typography
        variant="body2"
        sx={(theme) => ({
          ...getLabelStyles(theme),
          flexShrink: 0,
          minWidth: 'fit-content',
          fontWeight: 500,
        })}
      >
        {label}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-end' }}
      >
        {isPassword ? (
          <AnimatedPasswordText
            value={value}
            isPassword={isPassword}
            showPassword={showPassword || false}
            copyable={copyable}
          />
        ) : (
          <Tooltip
            title={
              label === t('login:traders_login.login') ||
              value.length > PASSWORD_DISPLAY.TOOLTIP_THRESHOLD
                ? t('login:traders_login.full_text', {
                    defaultValue: '{{text}}',
                    text: value,
                  })
                : ''
            }
            placement="top"
            disableHoverListener={
              label !== t('login:traders_login.login') &&
              value.length <= PASSWORD_DISPLAY.TOOLTIP_THRESHOLD
            }
          >
            <Typography
              variant="body2"
              fontWeight={400}
              sx={(theme) => ({
                ...getPasswordTextStyles(theme, isPassword),
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              })}
            >
              {value}
            </Typography>
          </Tooltip>
        )}
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ flexShrink: 0 }}
        >
          {isPassword && onTogglePassword && (
            <Tooltip
              title={
                showPassword
                  ? t('login:traders_login.hide_password', {
                      defaultValue: 'Hide password',
                    })
                  : t('login:traders_login.show_password', {
                      defaultValue: 'Show password',
                    })
              }
            >
              <IconButton
                size="small"
                onClick={onTogglePassword}
                sx={getIconButtonStyles}
              >
                <SystemIcon
                  iconName={showPassword ? 'EyeSlash' : 'Eye'}
                  size="small"
                />
              </IconButton>
            </Tooltip>
          )}
          {copyable && value !== UnavailableValue.Dash && onCopy && (
            <Tooltip
              title={
                isCopied
                  ? t('login:traders_login.copied', { defaultValue: 'Copied!' })
                  : t('login:traders_login.copy_to_clipboard', {
                      defaultValue: 'Copy {{field}} to clipboard',
                      field: label.toLowerCase(),
                    })
              }
              placement="top"
            >
              <IconButton
                size="small"
                onClick={handleCopyClick}
                sx={getIconButtonStyles}
              >
                <SystemIcon iconName="Copy" size="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
